import { useEffect, useMemo, useState } from "react"
import {
  faCalendarDay,
  faEnvelope,
  faEye,
  faMoneyBillWave,
  faMotorcycle,
  faPhone,
  faTriangleExclamation,
  faUser,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ECourierStatusType } from "../../Enum/ECourierStatusType"
import { useGetCourier } from "../../hooks/Courier/useGetCourier"
import { courierMemory } from "../../Objects/Memory/Memory"
import { Stat } from "./Components/Stat"
import { CreateCourierPupop } from "./Pupop/CreateCourierPupop"
import { LinkCourierPupop } from "./Pupop/LinkCourierPupop"
import { CourierCard } from "./Components/CourierCard"
import { useConfirmation } from "../../context/useConfirmationContext"
import { useSaveCourier } from "../../hooks/Courier/useSaveCourier"
import { useTranslation } from "react-i18next"
import { useCourierPayment } from "../../hooks/Courier/useCourierPayment"
import { CourierFinancialSummary } from "../../Objects/DeliveryEntity/CourierFinancialSummary"
import { CourierPaymentRecord } from "../../Objects/DeliveryEntity/CourierPaymentRecord"
import { ECourierPaymentConfirmationStatus } from "../../Enum/ECourierPaymentConfirmationStatus"
import { CourierPaymentPupop } from "./Pupop/CourierPaymentPupop"
import { RestaurantPaymentStatementPupop } from "./Pupop/RestaurantPaymentStatementPupop"
import { CourierDeliveriesStatementPupop } from "./Pupop/CourierDeliveriesStatementPupop"
import { CourierActiveRoutePupop } from "./Pupop/CourierActiveRoutePupop"
import { CourierEntity } from "../../Objects/Courier/CourierEntity"
import { DetailChip } from "./Components/DetailChip"
import { InfoMetricCard } from "./Components/InfoMetricCard"
import { currentDay, currentMonthStart } from "../../Objects/Generics/Helps"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import Button from "../../components/Button/Button"
import InputField from "../../components/InputField/InputField"
import THEME from "../../configuration/Theme"
import { PopupHelpButton } from "../../components/Help/PopupHelpButton"
import { useOnboardingCreatePopup } from "../../hooks/useOnboardingCreatePopup"
import { useQuickActionPopup } from "../../hooks/useQuickActionPopup"

export default function CourierPartnersPage() {
  const { t } = useTranslation("frontend")

  const [showCreateModal, setShowCreateModal] = useState(false)

  useOnboardingCreatePopup(() => setShowCreateModal(true))

  const [showLinkModal, setShowLinkModal] = useState(false)

  const [showPaymentModal, setShowPaymentModal] = useState(false)

  const [showStatementModal, setShowStatementModal] = useState(false)

  const [selectedRow, setSelectedRow] = useState<CourierFinancialSummary | null>(null)

  const [selectedDeliveriesRow, setSelectedDeliveriesRow] = useState<CourierFinancialSummary | null>(null)

  const [selectedActiveRouteCourier, setSelectedActiveRouteCourier] = useState<CourierEntity | null>(null)

  const [statementRows, setStatementRows] = useState<CourierPaymentRecord[]>([])

  const { handleGetCourier } = useGetCourier()

  const { setNewConfirmation } = useConfirmation()

  const { handleTryUnlinkCourierLinkAsync } = useSaveCourier()

  const { handleGetRestaurantPaymentControl, handleGetRestaurantPaymentStatement } = useCourierPayment()

  const [onlineCount, setOnlineCount] = useState<number>(0)

  const [offlineCount, setOfflineCount] = useState<number>(0)

  const [total, setTotal] = useState<number>(0)

  const [lastUpdateDate, setLastUpdateDate] = useState<string>(Date().toReadableDate())

  const [paymentRows, setPaymentRows] = useState<CourierFinancialSummary[]>([])

  const [activeTab, setActiveTab] = useState<"partners" | "payments">("partners")

  useQuickActionPopup("courier-link", () => setShowLinkModal(true))

  useQuickActionPopup("courier-payments", () => setActiveTab("payments"))

  const [statementFilter, setStatementFilter] = useState({ startDate: currentMonthStart(), endDate: currentDay() })

  const calculate = () => {
    const couriersTotal = courierMemory.listData.length

    const on = courierMemory.listData.filter((e) => e.status === ECourierStatusType.Online).length

    const off = couriersTotal - on

    setOnlineCount(on)

    setOfflineCount(off)

    setTotal(couriersTotal)

    setLastUpdateDate(Date().toReadableDate())
  }

  const paymentOverview = useMemo(() => {
    return paymentRows.reduce(
      (acc, row) => {
        acc.outstanding += row.outstandingAmount
        acc.rejected += row.rejectedAmount
        acc.pending += row.pendingConfirmationAmount
        return acc
      },
      { outstanding: 0, rejected: 0, pending: 0 },
    )
  }, [paymentRows])

  const refreshCouriers = () => {
    handleGetCourier(() => calculate())
  }

  const refreshPayments = (filter: { startDate?: string; endDate?: string } = {}) => {
    handleGetRestaurantPaymentControl(filter, setPaymentRows)
  }

  const refreshStatement = (filter: { startDate?: string; endDate?: string } = {}) => {
    handleGetRestaurantPaymentStatement(filter, setStatementRows)
  }

  const refreshAll = () => {
    refreshCouriers()

    refreshPayments(statementFilter)
  }

  useEffect(() => {
    refreshCouriers()

    const interval = setInterval(() => {
      refreshCouriers()

      if (activeTab === "payments") {
        refreshPayments({ startDate: statementFilter.startDate, endDate: statementFilter.endDate })
      }
    }, 30 * 1000)

    return () => clearInterval(interval)
  }, [activeTab, statementFilter.startDate, statementFilter.endDate])

  useEffect(() => {
    if (activeTab === "payments") {
      refreshPayments(statementFilter)
    }
  }, [activeTab, statementFilter.startDate, statementFilter.endDate])

  function handleUnlinkCourier(id: string) {
    setNewConfirmation(t("courier_partners.unlink_title"), t("courier_partners.unlink_description"), async () => {
      await handleTryUnlinkCourierLinkAsync(id, refreshAll)
    })
  }

  const handleOpenStatement = () => {
    refreshStatement(statementFilter)

    setShowStatementModal(true)
  }

  const handleOpenDeliveries = (row: CourierFinancialSummary) => {
    setSelectedDeliveriesRow(row)
  }

  return (
    <div className="flex h-[calc(100%-25px)] w-full flex-col overflow-y-auto p-1">
      <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
        <div className="px-4 py-4 space-y-4">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="w-full">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-2xl font-semibold text-slate-900">{t("courier_partners.title")}</h2>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="rounded-md bg-gradient-to-r from-orange-500 to-red-500 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:opacity-60"
                    data-interaction-description-key="interactionDescriptions:pages.delivery.courierPartnersPage.button.setShowCreateModalTrue"
                  >
                    {t("courier_partners.new_courier")}
                  </button>

                  <button
                    onClick={() => setShowLinkModal(true)}
                    className="rounded-md px-5 py-2.5 text-sm font-medium text-orange-600 ring-2 ring-orange-500 transition hover:ring-orange-800"
                    data-interaction-description-key="interactionDescriptions:pages.delivery.courierPartnersPage.button.setShowLinkModalTrue"
                  >
                    {t("courier_partners.link_existing")}
                  </button>
                </div>
              </div>

              <div className="mt-3 h-1 w-20 rounded-full bg-gradient-to-r from-orange-500 to-red-500" />

              <p className="mt-4 max-w-3xl text-sm text-slate-500">{t("courier_partners.description")}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Stat title="Total" value={total} accent="orange" />

            <Stat title="Online" value={onlineCount} accent="green" />

            <Stat title="Offline" value={offlineCount} accent="red" />
          </div>

          <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-slate-700">
              O pagamento do entregador é responsabilidade do restaurante. O sistema apenas organiza entregas
              concluídas, lançamentos manuais e conferências feitas pelas partes.
            </div>
          </Animate>

          <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
            <section className="overflow-hidden rounded-lg border-2 border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-4 py-4">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setActiveTab("partners")}
                    className={`rounded-md px-4 py-2 text-sm font-semibold transition ${activeTab === "partners" ? "bg-orange-500 text-white shadow-sm" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                    data-interaction-description-key="interactionDescriptions:pages.delivery.courierPartnersPage.button.setActiveTabPartners"
                  >
                    Entregadores
                  </button>

                  <button
                    onClick={() => {
                      refreshPayments(statementFilter)
                      setActiveTab("payments")
                    }}
                    className={`rounded-md px-4 py-2 text-sm font-semibold transition ${activeTab === "payments" ? "bg-orange-500 text-white shadow-sm" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                    data-interaction-description-key="interactionDescriptions:pages.delivery.courierPartnersPage.button.pagamentos"
                  >
                    Pagamentos
                  </button>

                  <PopupHelpButton
                    helpKey="pages.delivery.courierpartnerspage.1"
                    variant="global"
                    title={t("helpPopup:global.deliveryPartners.title")}
                    description={t("helpPopup:global.deliveryPartners.description")}
                    bullets={t("helpPopup:global.deliveryPartners.bullets", { returnObjects: true }) as string[]}
                  />
                </div>
              </div>

              {activeTab === "partners" && (
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <div>
                    <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                      <span className="text-sm font-semibold text-slate-700">
                        {t("courier_partners.linked_count", { count: total })}
                      </span>

                      <div className="ml-5 flex items-center gap-2">
                        <span className="text-xs font-bold text-orange-600 mr-2">{lastUpdateDate}</span>

                        <PopupHelpButton
                          helpKey="pages.delivery.courierpartnerspage.2"
                          title={t("helpPopup:sections.deliveryPartners.partners.title")}
                          description={t("helpPopup:sections.deliveryPartners.partners.description")}
                          bullets={
                            t("helpPopup:sections.deliveryPartners.partners.bullets", {
                              returnObjects: true,
                            }) as string[]
                          }
                        />
                      </div>
                    </div>

                    {total === 0 && <div className="p-6 text-sm text-slate-400">{t("courier_partners.empty")}</div>}

                    <div className="divide-y divide-slate-200">
                      <div className="grid gap-4 p-1">
                        {courierMemory.listData.map((courier, index) => (
                          <Animate
                            key={courier.id + index}
                            type={AnimationTypes.fadeInTraslate}
                            duration={`${0.2 + index}s`}
                          >
                            <CourierCard
                              courier={courier}
                              onUnlink={handleUnlinkCourier}
                              onViewActiveRoute={setSelectedActiveRouteCourier}
                            />
                          </Animate>
                        ))}
                      </div>
                    </div>
                  </div>
                </Animate>
              )}

              {activeTab === "payments" && (
                <Animate type={AnimationTypes.fadeInTraslate}>
                  <div className="space-y-4 p-4">
                    <div className="grid gap-4 xl:items-start">
                      <div>
                        <h3 className="text-base font-bold text-slate-900">Controle de pagamentos por entregador</h3>

                        <p className="text-xs text-slate-500">
                          Visualize o saldo, regularize contestações e consulte o extrato completo do restaurante.
                        </p>
                      </div>

                      <div className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div className="grid gap-3 lg:grid-cols-3 items-end">
                          <InputField
                            title="Data inicial"
                            type="date"
                            value={statementFilter.startDate}
                            onChange={(e) => setStatementFilter({ ...statementFilter, startDate: e.target.value })}
                            placeholder="Data inicial"
                            icon={faCalendarDay}
                            descriptionKey="interactionDescriptions:pages.delivery.courierPartnersPage.input.dataInicial"
                          />

                          <InputField
                            title="Data final"
                            type="date"
                            value={statementFilter.endDate}
                            onChange={(e) => setStatementFilter({ ...statementFilter, endDate: e.target.value })}
                            placeholder="Data final"
                            icon={faCalendarDay}
                            descriptionKey="interactionDescriptions:pages.delivery.courierPartnersPage.input.dataFinal"
                          />

                          <Button
                            bgColor={THEME.colors.buttonBGSaveColor}
                            label={"Ver extrato"}
                            onClick={handleOpenStatement}
                            descriptionKey="interactionDescriptions:pages.delivery.courierPartnersPage.button.verExtrato"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-3">
                      <InfoMetricCard
                        title="Saldo total em aberto"
                        value={paymentOverview.outstanding.toMoney()}
                        accent="red"
                      />

                      <InfoMetricCard title="Contestados" value={paymentOverview.rejected.toMoney()} accent="orange" />

                      <InfoMetricCard
                        title="Aguardando confirmação"
                        value={paymentOverview.pending.toMoney()}
                        accent="blue"
                      />
                    </div>

                    {paymentRows.length === 0 && (
                      <p className="text-sm text-slate-500">
                        Nenhum valor em aberto ou pagamento lançado para os entregadores vinculados.
                      </p>
                    )}

                    <div className="grid gap-4 xl:grid-cols-2">
                      {paymentRows.map((row) => {
                        const showRejectedAlert = row.rejectedAmount > 0 && row.outstandingAmount > 0

                        return (
                          <div
                            key={`${row.companyId}-${row.courierId}`}
                            className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm"
                          >
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                              <div className="flex items-start gap-3">
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 text-orange-600">
                                  <FontAwesomeIcon icon={faMotorcycle} />
                                </div>

                                <div>
                                  <h4 className="text-lg font-bold text-slate-900">{row.courierName}</h4>

                                  <p className="text-xs text-slate-500">
                                    {row.deliveredCount} entregas concluídas • em aberto{" "}
                                    {row.outstandingAmount.toMoney()}
                                  </p>
                                </div>
                              </div>

                              <Button
                                bgColor={THEME.colors.buttonBGSaveColor}
                                label={showRejectedAlert ? "Regularizar pagamento" : "Quitar dívida"}
                                onClick={() => {
                                  setSelectedRow(row)
                                  setShowPaymentModal(true)
                                }}
                                descriptionKey="interactionDescriptions:pages.delivery.courierPartnersPage.button.showRejectedAlertRegularizarPagamentoQuitarDivida"
                              />
                            </div>

                            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-2">
                              <DetailChip
                                icon={faEnvelope}
                                label="E-mail"
                                value={row.courierEmail || "Não informado"}
                              />

                              <DetailChip icon={faPhone} label="Telefone" value={row.courierPhone || "Não informado"} />

                              <DetailChip icon={faUser} label="Entregador" value={row.courierName} />

                              <DetailChip
                                icon={faMoneyBillWave}
                                label="Restaurante deve"
                                value={row.outstandingAmount.toMoney()}
                                highlight
                              />
                            </div>

                            <div className="mt-4 flex flex-wrap justify-end gap-2">
                              <button
                                onClick={() => handleOpenDeliveries(row)}
                                className="inline-flex items-center justify-center gap-2 rounded-xl border border-orange-300 bg-gradient-to-r from-orange-500 to-red-500 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:opacity-90 hover:shadow-md"
                                data-interaction-description-key="interactionDescriptions:pages.delivery.courierPartnersPage.button.handleOpenDeliveriesRow"
                              >
                                <FontAwesomeIcon icon={faEye} />
                                Ver entregas
                              </button>
                            </div>

                            {showRejectedAlert && (
                              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                                <div className="flex items-start gap-3">
                                  <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5" />

                                  <div>
                                    <p className="font-bold">O entregador contestou um ou mais pagamentos.</p>

                                    <p className="mt-1">
                                      Esse valor voltou para o saldo em aberto e precisa ser regularizado antes de
                                      qualquer novo pagamento. Após a regularização ser aceita, este aviso deixa de
                                      aparecer.
                                    </p>
                                  </div>
                                </div>

                                <div className="mt-3 space-y-2">
                                  {row.payments
                                    .filter(
                                      (payment) =>
                                        payment.confirmationStatus === ECourierPaymentConfirmationStatus.Rejected,
                                    )
                                    .map((payment) => (
                                      <div
                                        key={payment.id}
                                        className="rounded-md border border-red-100 bg-white px-3 py-2 text-xs text-slate-700"
                                      >
                                        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                                          <span className="font-semibold">
                                            Pagamento contestado: {payment.amount.toMoney()}
                                          </span>

                                          <span>
                                            {(
                                              payment.confirmationDatetime ?? payment.paymentDate
                                            ).toLocaleDateTimeOrDash()}
                                          </span>
                                        </div>

                                        {payment.notes && (
                                          <p className="mt-1 text-slate-500">Observação: {payment.notes}</p>
                                        )}
                                      </div>
                                    ))}
                                </div>
                              </div>
                            )}

                            <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-5 text-sm">
                              {[
                                ["Elegível", row.deliveredAmount],
                                ["Pago", row.paidAmount],
                                ["Confirmado", row.confirmedAmount],
                                ["Contestados", row.rejectedAmount],
                                ["Aguardando", row.pendingConfirmationAmount],
                              ].map(([label, value]) => (
                                <div key={String(label)} className="rounded-xl bg-slate-50 p-3">
                                  <p className="text-xs uppercase text-slate-500">{label}</p>
                                  <strong className="text-slate-900">{Number(value).toMoney()}</strong>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </Animate>
              )}
            </section>
          </Animate>
        </div>
      </Animate>

      {showCreateModal && <CreateCourierPupop onClonse={() => setShowCreateModal(false)} onSuccess={refreshAll} />}

      {showLinkModal && <LinkCourierPupop onClonse={() => setShowLinkModal(false)} onSuccess={refreshAll} />}

      {showPaymentModal && selectedRow && (
        <CourierPaymentPupop
          row={selectedRow}
          onClose={() => setShowPaymentModal(false)}
          onSaved={() => {
            setShowPaymentModal(false)
            refreshAll()
          }}
        />
      )}

      {showStatementModal && (
        <RestaurantPaymentStatementPupop
          rows={statementRows}
          filter={statementFilter}
          onFilterChange={setStatementFilter}
          onClose={() => setShowStatementModal(false)}
          onSearch={() => refreshStatement(statementFilter)}
        />
      )}

      {selectedDeliveriesRow && (
        <CourierDeliveriesStatementPupop
          title={`Entregas`}
          description={`Entregas consideradas no controle financeiro entre ${statementFilter.startDate.toString().toReadableDate()} e ${statementFilter.endDate.toString().toReadableDate()} para este entregador.`}
          counterpartName={selectedDeliveriesRow.courierName}
          deliveries={selectedDeliveriesRow.deliveries}
          onClose={() => setSelectedDeliveriesRow(null)}
        />
      )}

      {selectedActiveRouteCourier && (
        <CourierActiveRoutePupop
          courierId={selectedActiveRouteCourier.id}
          courierName={selectedActiveRouteCourier.personEntity.name}
          onClose={() => setSelectedActiveRouteCourier(null)}
        />
      )}
    </div>
  )
}
